import React, { useRef, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { gsap } from 'gsap';
import ReactPlayer from 'react-player';

//Components
import Image from './image';

const Intro = ({ data, lang }) => {
  const [show, setShow] = useState(false);
  const [video, setVideo] = useState(false);
  const [mobile, setMobile] = useState(false);
  const introRef = useRef(null);
  const introMediaRef = useRef(null);
  const introMediaOverlayRef = useRef(null);
  const introMediaItemRef = useRef(null);
  const introVideoRef = useRef(null);
  const introTitle = useRef(null);
  const introEntry = useRef(null);

  const videoBtnLabel = language => {
    switch (language) {
      case 'bg':
        return 'Вижте повече';
      case 'en':
        return 'Take a look inside';
      case 'de':
        return 'Schau hinein';
      default:
        return '';
    }
  };

  const scrollToNext = () => {
    window.scrollTo({
      top: introRef.current.nextSibling.offsetTop,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleVideoTrigger = bool => {
    setVideo(bool);
    gsap.to(introVideoRef.current, {
      duration: 0.525,
      opacity: bool ? 1 : 0,
      visibility: bool ? 'visible' : 'hidden',
      ease: 'power2.easeOut',
    });
  };

  useEffect(() => {
    const checkMobile = () => setMobile(window.innerWidth < 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (data.media) {
      gsap.from(introMediaRef.current, {
        duration: 0.525,
        delay: 0.6,
        opacity: 0,
        yPercent: 25,
        force3D: true,
        ease: 'power2.easeOut',
      });

      gsap.to(introMediaOverlayRef.current, {
        duration: 0.9,
        delay: 0.85,
        scaleX: 0,
        ease: 'power0.easeOut',
      });

      gsap.from(introMediaItemRef.current, {
        duration: 0.9,
        delay: 0.85,
        xPercent: 15,
        opacity: 0,
        force3D: true,
        ease: 'power0.easeNone',
      });
    }

    gsap.from(introTitle.current, {
      duration: 0.4,
      delay: 0.6,
      opacity: 0,
      yPercent: 20,
      force3D: true,
    });

    gsap.from(introEntry.current, {
      duration: 0.4,
      delay: 0.6,
      opacity: 0,
      yPercent: 20,
      force3D: true,
    });
  }, []);

  if (!data) {
    return null;
  }

  return (
    <div
      ref={introRef}
      className="intro py-7 pt-md-11 pt-lg-15 pt-xl-19 pt-mg-27 pb-md-8 pb-lg-10 pb-xl-14 pb-mg-17"
    >
      <div
        className={`container ${
          data.media ? 'mb-6 mb-md-10 mb-lg-15 mb-mg-28' : ''
        }`}
      >
        <div className="pl-xl-15">
          <h1 ref={introTitle} className="text-hero mb-3">
            {data.title}
          </h1>

          {data.text && (
            <div className="row">
              <div className="col-lg-7">
                <h5 ref={introEntry} className="text-title font-weight-normal">
                  <ReactMarkdown
                    source={data.text}
                    disallowedTypes={['paragraph']}
                    unwrapDisallowed
                  />
                </h5>
              </div>
            </div>
          )}
        </div>
      </div>

      {data.media && (
        <div ref={introMediaRef} className="intro__media d-md-flex ml-auto">
          <button
            onClick={scrollToNext}
            className="intro__scroll-down d-none d-md-flex"
          >
            <i className="fal fa-long-arrow-down" aria-hidden="true"></i>
          </button>

          {data.media && (
            <div ref={introMediaItemRef} className="intro__media-item">
              <div
                ref={introMediaOverlayRef}
                className="intro__media-overlay"
              ></div>

              {data.thumbnailVideo && !mobile && (
                <ReactPlayer
                  url={data.thumbnailVideo}
                  width="120%"
                  height="120%"
                  className="intro__video-thumbnail"
                  style={{ opacity: show ? 1 : 0 }}
                  playing={true}
                  loop={true}
                  volume={0}
                  muted={true}
                  controls={false}
                  onStart={() => setTimeout(() => setShow(true), 500)}
                />
              )}

              {data.fullVideo && !mobile && (
                <button
                  className="intro__video-btn btn btn-primary"
                  onClick={() => handleVideoTrigger(true)}
                >
                  <i className="far fa-play mr-md-4"></i>

                  <span className="d-none d-md-inline-block">
                    {videoBtnLabel(lang)}
                  </span>
                </button>
              )}

              <Image image={data.media} alt={data.title} />
            </div>
          )}
        </div>
      )}

      {data.fullVideo && !mobile && (
        <div ref={introVideoRef} className="intro__video">
          <button
            onClick={() => handleVideoTrigger(false)}
            className="intro__video-close btn p-0 d-flex align-items-center"
          >
            <span
              className="text-small font-weight-medium"
              style={{ pointerEvents: 'none' }}
            >
              CLOSE
            </span>
            <i
              className="fal fa-times ml-3 text-danger"
              aria-hidden="true"
              style={{ pointerEvents: 'none' }}
            ></i>
          </button>

          <div className="intro__video-inner">
            <div className="intro__video-responsive">
              <ReactPlayer
                url={data.fullVideo}
                width="100%"
                height="100%"
                className="intro__video-player"
                playing={video}
                controls={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Intro;
